import { Component, ElementRef, HostBinding, HostListener, Inject, inject, OnDestroy, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NgxSmartModalService } from "ngx-smart-modal";
import { LoginEndconsumerResponseData, LogoutRequestData, EndconsumerModel } from "../../models/user.model";
import { UserService } from "../../services/user.service";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { ActiveTab, TabService } from "../../services/tab-service.service";
import { TranslateService } from "@ngx-translate/core";
import { LANGS, LanguageService } from "../../services/language.service";
import { AuthService } from "../../services/auth.service";
import { Subscription } from "rxjs";
import { STORE_LINKS, Storefront } from '../../constants/store-links';
import { ProfileService } from "../../services/profile.service";

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  private _ngxSmartModalService: NgxSmartModalService = inject(NgxSmartModalService);
  private _userService: UserService = inject(UserService);
  private _router: Router = inject(Router);
  private _renderer: Renderer2 = inject(Renderer2);
  private _el: ElementRef = inject(ElementRef);
  private _tabService: TabService = inject(TabService);
  private _translateService: TranslateService = inject(TranslateService);
  private _languageService: LanguageService = inject(LanguageService);
  private _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private _authService: AuthService = inject(AuthService);
  private _profileService: ProfileService = inject(ProfileService);

  userData!: any; // TODO EndconsumerModel or TeamModel
  loginData!: LoginEndconsumerResponseData | '';
  showNavBar: boolean = false;
  scrolled: boolean = false;
  overLightBackground: boolean = false;
  private readonly HEADER_ZONE_PX = 120;
  activeRoute!: string;
  ACTIVE_TAB = ActiveTab;
  activeTab!: ActiveTab;
  mainNavigation: Record<ActiveTab, NavItemModel[]>  = { GIPANT: [], HENTERPANT: [], TEAMS_OR_PLAYERS: [] };
  LANGS = LANGS;
  currentLanguage!: string;
  expandedItems: Set<string> = new Set();

  activeAnchor: string | null = null;
  observer!: IntersectionObserver;
  langSubscription!: Subscription;
  userSub!: Subscription;
  profileSub!: Subscription;
  activeProfile: any = null;
  private _platformId: Object = inject(PLATFORM_ID);
  private readonly storeLinks = STORE_LINKS;

  @HostBinding('class.active-tab-gipant') get isGipantTab(): boolean {
    return this.activeTab === ActiveTab.GIPANT || this.activeTab === ActiveTab.TEAMS_OR_PLAYERS;
  }

  @HostBinding('class.active-tab-henterpant') get isHenterpantTab(): boolean {
    return this.activeTab === ActiveTab.HENTERPANT;
  }

  constructor() {
  }

  ngOnInit(): void {
    this.currentLanguage = this._languageService.currentLang;

    this.setMainNavigation();

    setTimeout(() => {
      this.activeTabSubscription();
      this.userDataSubscription();
      this.profileSubscription();
      this.routerSubscription();
      this.languageSubscription();
    })

    // this._activatedRoute.fragment.subscribe(fragment => {
    //   if (fragment) {
    //     setTimeout(() => {
    //       this.scrollToAnchor(fragment);
    //     }, 0);
    //   }
    // });

    this.initActiveAnchor();
    this.checkOverLightBackground();
  }

  /**
   * When the header is over a block with data-header-contrast="light" (e.g. white background),
   * we switch to a dark header for contrast. Pages without such blocks (e.g. home) stay default.
   */
  checkOverLightBackground(): void {
    if (!isPlatformBrowser(this._platformId)) return;
    const lightBlocks = document.querySelectorAll('[data-header-contrast="light"]');
    let over = false;
    lightBlocks.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < this.HEADER_ZONE_PX && rect.bottom > 0) {
        over = true;
      }
    });
    if (over !== this.overLightBackground) {
      this.overLightBackground = over;
      if (this.overLightBackground) {
        this._renderer.addClass(this._el.nativeElement, 'over-light-bg');
      } else {
        this._renderer.removeClass(this._el.nativeElement, 'over-light-bg');
      }
    }
  }

  initActiveAnchor() {
    if (!isPlatformBrowser(this._platformId)) return;
    setTimeout(() => {
      const sections = document.querySelectorAll('section');

      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const anchorEl = entry.target.querySelector('a.anchor[id]');
            if (anchorEl && anchorEl.id) {
              this.activeAnchor = anchorEl.id;
            }
          }
        });
      }, {
        threshold: 0.5
      });

      sections.forEach(section => this.observer.observe(section));
      this.checkOverLightBackground();
    }, 100);
  }

  setMainNavigation(): void {
    this._translateService.get([
      'NAV.GIPANT.HOME',
      'NAV.GIPANT.PICK_UP_REQUEST',
      'NAV.GIPANT.HOW_IT_WORKS',
      'NAV.GIPANT.FEATURES',
      'NAV.GIPANT.ABOUT_US',
      'NAV.GIPANT.CONTACT_US',
      'NAV.GIPANT.LEGAL',
      'NAV.GIPANT.LEGAL_TERMS',
      'NAV.GIPANT.LEGAL_PP',
      'NAV.GIPANT.LEGAL_CANCELLATION',

      'NAV.HENTERPANT.HOME',
      'NAV.HENTERPANT.SLIK_FUNGERER_DET',
      'NAV.HENTERPANT.GET_STARTED',
      'NAV.HENTERPANT.GUIDANCE',
      'NAV.HENTERPANT.HOW_IT_WORKS',
      'NAV.HENTERPANT.FEATURES',
      'NAV.HENTERPANT.ABOUT_US',
      'NAV.HENTERPANT.CONTACT_US',
      'NAV.HENTERPANT.LEGAL',
      'NAV.HENTERPANT.LEGAL_TERMS',
      'NAV.HENTERPANT.LEGAL_PP',
      'NAV.HENTERPANT.LEGAL_COMPLIANCE_SUITE',
      'NAV.HENTERPANT.LEGAL_CANCELLATION',
      'NAV.HENTERPANT.LEGAL_FAQ',
      'NAV.HENTERPANT.LEGAL_DPA'
    ]).subscribe(translations => {
      const currentLang: string = this._translateService.currentLang;

      this.mainNavigation = {
        GIPANT: [
          { title: translations['NAV.GIPANT.HOME'], anchor: `home` },
          { title: translations['NAV.GIPANT.PICK_UP_REQUEST'], anchor: `pick-up-request` },
          { title: translations['NAV.GIPANT.HOW_IT_WORKS'], anchor: `how-it-works` },
          { title: translations['NAV.GIPANT.FEATURES'], anchor: `features` },
          { title: translations['NAV.GIPANT.ABOUT_US'], anchor: `about-us` },
          { title: translations['NAV.GIPANT.CONTACT_US'], anchor: `contact-us` },
          {
            title: translations['NAV.GIPANT.LEGAL'],
            childrens: [
              {title: translations['NAV.GIPANT.LEGAL_TERMS'], route: `${currentLang}/terms-and-conditions`},
              {title: translations['NAV.GIPANT.LEGAL_PP'], route: `${currentLang}/privacy-policy`}
            ]
          }
        ],
        HENTERPANT: [
          { title: translations['NAV.HENTERPANT.SLIK_FUNGERER_DET'], route: `${currentLang}/henter-pant/slik-fungerer-det` },
          { title: translations['NAV.HENTERPANT.GET_STARTED'], route: `${currentLang}/henter-pant` },
          { title: translations['NAV.HENTERPANT.GUIDANCE'], route: `${currentLang}/henter-pant`, anchor: `contact-us` },
          { title: translations['NAV.HENTERPANT.LEGAL_FAQ'], route: `${currentLang}/henter-pant/hjelp` },
          {
            title: translations['NAV.HENTERPANT.LEGAL'],
            childrens: [
              {title: translations['NAV.HENTERPANT.LEGAL_COMPLIANCE_SUITE'], route: `${currentLang}/henter-pant/compliance-suite`},
              {title: translations['NAV.HENTERPANT.LEGAL_TERMS'], route: `${currentLang}/henter-pant/terms-and-conditions`},
              {title: translations['NAV.HENTERPANT.LEGAL_PP'], route: `${currentLang}/henter-pant/privacy-policy`},
              {title: translations['NAV.HENTERPANT.LEGAL_CANCELLATION'], route: `/assets/legal/Angrerettskjema–HenterPant-abonnement.pdf`, download: true},
              {title: translations['NAV.HENTERPANT.LEGAL_DPA'], route: `/assets/legal/DPA-HenterPant.pdf`, download: true}
            ]
          }
        ],
        TEAMS_OR_PLAYERS: [
          { title: translations['NAV.GIPANT.HOME'], anchor: `home` },
          { title: translations['NAV.GIPANT.PICK_UP_REQUEST'], anchor: `pick-up-request` },
          { title: translations['NAV.GIPANT.HOW_IT_WORKS'], anchor: `how-it-works` },
          { title: translations['NAV.GIPANT.FEATURES'], anchor: `features` },
          { title: translations['NAV.GIPANT.ABOUT_US'], anchor: `about-us` },
          { title: translations['NAV.GIPANT.CONTACT_US'], anchor: `contact-us` },
          {
            title: translations['NAV.GIPANT.LEGAL'],
            childrens: [
              {title: translations['NAV.GIPANT.LEGAL_TERMS'], route: `${currentLang}/terms-and-conditions`},
              {title: translations['NAV.GIPANT.LEGAL_PP'], route: `${currentLang}/privacy-policy`}
            ]
          }
        ]
      };
    });
  }

  toggleExpand(navItem: NavItemModel): void {
    if (this.expandedItems.has(navItem.title)) {
      this.expandedItems.delete(navItem.title);
    } else {
      this.expandedItems.add(navItem.title);
    }
  }

  handleClick(navItem: NavItemModel, event?: any): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (navItem.childrens?.length) {
      this.toggleExpand(navItem);
    } else if (navItem.download && navItem.route) {
      const link = document.createElement('a');
      link.href = navItem.route;
      link.download = '';
      link.target = '_blank';
      link.click();
    } else if (navItem.route && !navItem.anchor) {
      this.goToPage(navItem.route, null, navItem.fragment);
    } else if (navItem.anchor) {
      this.scrollToAnchor(navItem.anchor, false, navItem.route);
    }
  }

  activeTabSubscription(): void {
    this._tabService.activeTab$.subscribe(tab => {
      this.activeTab = tab;

      this.initActiveAnchor();
    });
  }

  userDataSubscription(): void {
    this.userSub = this._userService.user$.subscribe((userData: LoginEndconsumerResponseData | '') => {
      this.loginData = userData;

      if (this.loginData && this.loginData.user) {
        this.userData = this.loginData.user;
      } else {
        this.userData = null;
      }
    });
  }

  profileSubscription(): void {
    this.profileSub = this._profileService.activeProfile$.subscribe(profile => {
      this.activeProfile = profile;
    });
  }

  routerSubscription(): void {
    this._router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.activeRoute = event.urlAfterRedirects.split('#')[0];

        this.activeAnchor = null;
        setTimeout(() => this.checkOverLightBackground(), 150);
      }
    });
  }

  languageSubscription(): void {
    this.langSubscription = this._languageService.onLangChange$.subscribe((event: any) => {
      this.currentLanguage = event.lang;
      this.setMainNavigation();
      this.initActiveAnchor();
    });
  }

  logout(): void {
    if (!this.loginData) return;

    const requestData: LogoutRequestData = {
      refresh: this.loginData.tokens.refresh
    }

    this._userService.logout(requestData).subscribe(
      result => {
        this.removeUserAndAccessToken();
      },
      error => {
        const errorDetail: string | undefined = error?.error?.detail ?? error?.detail;
        const errorCode: string | undefined = error?.error?.code ?? error?.code;

        if (
          errorDetail === 'Invalid token or token already blacklisted.' ||
          errorDetail === 'User not found' ||
          errorCode === 'user_not_found'
        ) {
          this.removeUserAndAccessToken();
        }
      }
    );
  }

  removeUserAndAccessToken(): void {
    this._userService.delete();
    this._authService.clearAccessToken();
  }

  toggleNavBar(): void {
    this.showNavBar = !this.showNavBar;
  }

  get appleStoreLink(): string {
    return this.storeLinks[this.currentStorefront].appStore;
  }

  get googlePlayLink(): string {
    return this.storeLinks[this.currentStorefront].googlePlay;
  }

  private get currentStorefront(): Storefront {
    if (this.activeTab === ActiveTab.HENTERPANT) {
      return 'HENTERPANT';
    }

    return 'GIPANT';
  }

  goToPage(route: string, queryParams?: any, fragment?: string, event?: any): void {
    if (queryParams) {
      this._router.navigate(route.split('/'), { queryParams });
    } else {
      this._router.navigate(route.split('/'));
    }

    setTimeout(() => {
      this.toggleNavBar();
    }, 500);
  }

  scrollToAnchor(anchorId: string, skipToggle: boolean = false, route?: string): void {
    const currentLang: string = this._translateService.currentLang;
    let baseRoute: string = route || '';

    if (this.activeTab === this.ACTIVE_TAB.GIPANT && !baseRoute) baseRoute = `/${currentLang}`;
    if (this.activeTab === this.ACTIVE_TAB.HENTERPANT && !baseRoute) baseRoute = `/${currentLang}/henter-pant`;

    if (isPlatformBrowser(this._platformId)) {
      setTimeout(() => {
        const el = document.getElementById(anchorId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 450);
    }

    this._router.navigate([baseRoute], { fragment: anchorId }).then(() => {
      if (isPlatformBrowser(this._platformId)) {
        setTimeout(() => {
          const el = document.getElementById(anchorId);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 0);
      }
    });

    if (!skipToggle) {
      setTimeout(() => {
        this.toggleNavBar();
      }, 500);
    }
  }

  openProfile(): void {
    const currentLang: string = this._translateService.currentLang;
    const profileRoute: string = `/${currentLang}/henter-pant/profile`;
    this.goToPage(profileRoute);
  }

  openSignInSignUp(path: string): void {
    const currentLang: string = this._translateService.currentLang;
    const profileRoute: string = `/${currentLang}/henter-pant/profile`;

    const queryParams = path === 'sign-in'
      ? { tab: 'sign-in' }
      : { tab: 'sign-up' };

    this.goToPage(profileRoute, queryParams);
  }

  openHomePage(route: string): void {
    this._userService.changeCurrentUser( route === 'team' ? 'team' : 'end-consumer');
    const currentLang: string = this._translateService.currentLang;
    this._router.navigate([`/${currentLang}/${route}`]);
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    if (!isPlatformBrowser(this._platformId)) return;
    const scrollPosition: number = window.scrollY || document.documentElement.scrollTop;

    if (scrollPosition > 50) {
      this.scrolled = true;
      this._renderer.addClass(this._el.nativeElement, 'scrolled');
    } else {
      this.scrolled = false;
      this._renderer.removeClass(this._el.nativeElement, 'scrolled');
    }
    this.checkOverLightBackground();
  }

  @HostListener('window:resize', [])
  onResize(): void {
    this.checkOverLightBackground();
  }

  setLanguage(lang: string): void {
    this._languageService.setLanguage(lang, true);
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.langSubscription) this.langSubscription.unsubscribe();
    if (this.userSub) this.userSub.unsubscribe();
    if (this.profileSub) this.profileSub.unsubscribe();
  }
}

export interface NavItemModel {
  title: string;
  route?: string;
  anchor?: string;
  download?: boolean;
  fragment?: string;
  childrens?: NavItemModel[];
}
